import type { TargetPrice } from '@app-types/performance';

export interface TargetPriceAxisRange {
  min: number;
  max: number;
  interval: number;
}

/** Preferred gridline count; the axis aims for this many intervals. */
const TARGET_TICK_COUNT = 5;
/** Hard cap so a tight range cannot produce a crowded axis. */
const MAX_TICK_INTERVALS = 6;
/** Round intervals to 1/2/5 x 10^n so labels stay readable. */
const NICE_MULTIPLIERS = [1, 2, 5] as const;
/** Guard against pathological loops when data is degenerate. */
const MAX_INTERVAL_STEPS = 12;

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/**
 * Decimal places needed so axis labels stay distinct for the given interval.
 * Yen prices use whole numbers; sub-unit intervals need decimals to avoid
 * rendering the same rounded label twice.
 */
export const getTargetPriceAxisFractionDigits = (interval: number): number => {
  if (!isFiniteNumber(interval) || interval <= 0 || interval >= 1) {
    return 0;
  }

  return Math.min(4, Math.ceil(-Math.log10(interval)));
};

/** Smallest 1/2/5 x 10^n value that is >= `minInterval`. */
const niceIntervalAtLeast = (minInterval: number): number => {
  if (!isFiniteNumber(minInterval) || minInterval <= 0) {
    return 1;
  }

  const startExponent = Math.floor(Math.log10(minInterval));
  for (let exponent = startExponent; exponent <= startExponent + 3; exponent += 1) {
    const magnitude = 10 ** exponent;
    for (const multiplier of NICE_MULTIPLIERS) {
      const candidate = multiplier * magnitude;
      if (candidate >= minInterval) {
        return candidate;
      }
    }
  }

  return 10 ** (startExponent + 1);
};

/**
 * Rounds the data range outward to whole intervals. When a data point lands
 * exactly on a boundary it is pushed out by one interval so markers and the
 * right-side label boxes never sit on the frame edge.
 */
const toBounds = (
  dataMin: number,
  dataMax: number,
  interval: number,
): { min: number; max: number } => {
  let min = Math.floor(dataMin / interval) * interval;
  let max = Math.ceil(dataMax / interval) * interval;

  if (min === dataMin) {
    min -= interval;
  }
  if (max === dataMax) {
    max += interval;
  }
  if (max <= min) {
    max = min + interval;
  }

  return { min, max };
};

/**
 * Computes a y-axis range for the target price chart from the actual values
 * (history line plus current/low/average/high targets).
 *
 * The range is derived from the data instead of fixed 100/500 steps, so it
 * scales correctly for any instrument price level. The interval is snapped to
 * 1/2/5 x 10^n, bounds are rounded outward, and the gridline count is capped so
 * the 175px chart never gets crowded.
 *
 * Always guarantees `min < max`, even when every value is equal.
 */
export const computeTargetPriceAxis = (targetPrice: TargetPrice): TargetPriceAxisRange => {
  const values = [
    ...targetPrice.history.map((point) => point.value),
    targetPrice.current,
    targetPrice.lowest,
    targetPrice.average,
    targetPrice.highest,
  ].filter(isFiniteNumber);

  if (values.length === 0) {
    return { min: 0, max: 100, interval: 20 };
  }

  let dataMin = Math.min(...values);
  let dataMax = Math.max(...values);

  if (dataMin === dataMax) {
    // Flat data: open a band proportional to the value so labels stay distinct.
    const delta = (Math.abs(dataMin) || 1) * 0.01;
    dataMin -= delta;
    dataMax += delta;
  }

  let interval = niceIntervalAtLeast((dataMax - dataMin) / TARGET_TICK_COUNT);
  let bounds = toBounds(dataMin, dataMax, interval);

  // Outward rounding can add gridlines; step the interval up until it fits.
  for (let step = 0; step < MAX_INTERVAL_STEPS; step += 1) {
    if ((bounds.max - bounds.min) / interval <= MAX_TICK_INTERVALS) {
      break;
    }
    interval = niceIntervalAtLeast(interval * 1.5);
    bounds = toBounds(dataMin, dataMax, interval);
  }

  return { min: bounds.min, max: bounds.max, interval };
};
